import { Injectable, OnDestroy } from "@angular/core";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { MatDialog } from "@angular/material/dialog";
import { Router } from "@angular/router";
import { Location } from "@angular/common";

import { BehaviorSubject, Subject } from "rxjs";
import { filter, takeUntil, tap } from "rxjs/operators";

import { StorageKeyEnum } from "../core/StorageKeyEnum";
import {
  LoginModel,
  ResetPasswordModel,
  InviteMembersModel,
  RegisterMemberModel,
  ChangePasswordModel,
  UpdateCompanyPayload,
} from "../models/login.model";
import { RegisterModel } from "../models/register.model";
import {
  hasPermission,
  permissionCount,
} from "../components/common-sidebar/nav-routes";
import { PaymentDialogComponent } from "../components/dialogs/payment-dialog/payment-dialog.component";
import { ConfirmDialogComponent } from "../components/dialogs/confirm-dialog/confirm-dialog.component";
import { Role } from "../models/user-role.model";

@Injectable({
  providedIn: "root",
})
export class AuthService  implements OnDestroy{
  public userInfo$ = new BehaviorSubject<LoginModel>(this.getUserInfo());
  private url = "api/user";
  private headers = new HttpHeaders({ "Content-Type": "application/json" });
  options = { headers: this.headers };
  public _tierIdObs$ = new BehaviorSubject(null);
  locationObs$ = new BehaviorSubject(null);
  public _orderAmounts = new BehaviorSubject(null);
  _isAccountActive = new BehaviorSubject(null);
  private destroy$ = new Subject<void>();
  tierChangeRoute: boolean = false;
  constructor(
    private http: HttpClient,
    private dialog: MatDialog,
    public router: Router,
    private location: Location
  ) {
    this._isAccountActive.pipe(takeUntil(this.destroy$)).subscribe((res)=>{
    if(!location.path().includes('settings/integrations'))  this.openActivateAccountPopup();
    })
  }

  public login(username: string, password: string) {
    const data = JSON.stringify({ username, password });
    return this.http.post(`/${this.url}/authenticate`, data, this.options).pipe(
      tap((usr: any) => {
        this.userInfo$.next(usr);
      })
    );
  }

  public loginByAdmin(adminCode: string) {
    const data = JSON.stringify({ adminCode });
    return this.http
      .post(`/${this.url}/authenticatebyadmin`, data, this.options)
      .pipe(
        tap((usr: any) => {
          this.userInfo$.next(usr);
        })
      );
  }

  public register(register: RegisterModel) {
    const data = JSON.stringify(register);
    return this.http.post(`/${this.url}/register`, data, this.options);
  }

  public registerMember(register: RegisterMemberModel) {
    const data = JSON.stringify(register);
    return this.http.post(`/${this.url}/register/member`, data, this.options);
  }

  public forgotPassword(email: string) {
    return this.http.get(
      `/${this.url}/forgotpassword/${email}/${encodeURIComponent(
        document.location.origin
      )}`,
      this.options
    );
  }

  public resetPassword(model: ResetPasswordModel) {
    return this.http.post(`/${this.url}/resetpassword`, model, this.options);
  }

  public changePassword(model: ChangePasswordModel) {
    return this.http.post(`/${this.url}/changepassword`, model, this.options);
  }

  public getAllRoles() {
    return this.http.get(`/${this.url}/getallroles`, this.options);
  }

  public inviteMembers(inviteMembersModel: InviteMembersModel) {
    return this.http.post(
      `/${this.url}/invite/members`,
      inviteMembersModel,
      this.options
    );
  }

  public verifyUsername(username: string) {
    return this.http.get(
      `/${this.url}/getuserbyname/${username}`,
      this.options
    );
  }

  public verifyEmail(email: string) {
    return this.http.get(`/${this.url}/getuserbyemail/${email}`, this.options);
  }

  public verifyMemberEmail(email: string) {
    return this.http.get(
      `/${this.url}/getmemberbyemail/${email}`,
      this.options
    );
  }

  public getUsersByTenantId() {
    return this.http.get(`/${this.url}/getusersbytenantid`, this.options);
  }

  public deleteUser(id: number) {
    return this.http.delete(`/${this.url}/deleteuser/${id}`, this.options);
  }

  public updateUser(request: any) {
    const data = JSON.stringify(request);
    return this.http.put(`/${this.url}/updateuser`, data, this.options);
  }

  public logout(skipNavaigation: boolean = false) {
   // localStorage.clear();
    localStorage.removeItem(StorageKeyEnum.User);
    localStorage.removeItem(StorageKeyEnum.SelectedAccount);
    localStorage.removeItem(StorageKeyEnum.PoFilters);
   // localStorage.removeItem(StorageKeyEnum.ProductsSubGridType);
   // localStorage.removeItem('toolPanel');
  //  localStorage.removeItem(StorageKeyEnum.Dimensions);

    (<any>window).Intercom("shutdown");
    this.userInfo$.next(undefined as LoginModel);

    if (!skipNavaigation) {
      this.router.navigate(["/account/login"]);
    }
  }

  public isLoggedIn(): boolean {
    return !!localStorage.getItem(StorageKeyEnum.User);
  }

  public getToken(): string {
    const tokenData = JSON.parse(
      localStorage.getItem(StorageKeyEnum.User)
    ) as LoginModel;
    if (tokenData) {
      return tokenData.token;
    } else {
      return null;
    }
  }

  public getUserInfo(): LoginModel {
    const tokenData = JSON.parse(
      localStorage.getItem(StorageKeyEnum.User)
    ) as LoginModel;
    if (tokenData) {
      return tokenData;
    } else {
      return null;
    }
  }

  public GetAllusers() {
    return this.http.get(`/${this.url}/GetUsersByTenantId`, this.options);
  }
  
  public GetAllUsersByTenantId() {
    return this.http.get(`/${this.url}/GetAllUsersByTenantId`, this.options);
  }

  public getTenantId() {
    const tokenData = JSON.parse(
      localStorage.getItem(StorageKeyEnum.User)
    ) as LoginModel;
    if (tokenData) {
      return tokenData.tenantId;
    } else {
      return null;
    }
  }

  public submitTicket(ticket: any) {
    return this.http.post(`/${this.url}/submitticket`, ticket, {
      headers: this.headers,
      observe: "response",
      responseType: "text",
    });
  }

  setTier(id: any): void {
    this._tierIdObs$.next(id);
  }
  get getTierObs(): any {
    return this._tierIdObs$;
  }
  openPaymentDialog() {
    var user = this.getUserInfo();    
    if ((user.balanceOverdue && user.balanceOverdue > 0) || (user.exceededTiers && user.exceededTiers.length > 0)) {
      this.dialog.open(PaymentDialogComponent, {
        width: "600px",
        closeOnNavigation: false,
        disableClose: true,
        data: {
          balanceOverdue: user.balanceOverdue,
          exceededTiers: user.exceededTiers,
        },
      });
    }
  }

  openActivateAccountPopup(){
    if(this._isAccountActive.value){
    this.dialog
    .open(ConfirmDialogComponent, {
      width: "600px",
      closeOnNavigation: false,
      disableClose: true,
        data: {
          title: "Update Amazon Connections",
          message: `<p><b>Amazon integrations need to be recertified every 12 months.</b></p><br>
          Please click “Go to Integrations” to find and reconnect the expired Amazon accounts.<br>
          It shouldn't take more than a few seconds.`,
          actions:[           
            { text: "Go to Integrations" },
          ]
        },
    })
    .afterClosed()
    .pipe(filter((e) => e === "Go to Integrations"))
    .subscribe((e) => {
        this.router.navigate(["/settings/integrations"]);
    });
  }
  }

  redirectRouteOnLoad(userInfo: LoginModel, returnUrl?: string) {
    if (userInfo) {
      const route = this.initialLoadRedirect(userInfo, returnUrl);
      this.router.navigate([route]);
      this.locationObs$.next(route);
    } else {
      this.router.navigate(["/account/login"]);
    }
  }

  checkOnboarding(userInfo: LoginModel) {
    if (userInfo) {
      if (!userInfo.onboardingStep || userInfo.onboardingStep === 1) {
        return "/onboarding/welcome";
      } else if (userInfo.onboardingStep === 2) {
        return "/onboarding/terms";
      } else if (
        userInfo.onboardingStep === 3 ||
        userInfo.onboardingStep === 4
      ) {
        return "/onboarding/products";
      } else if (userInfo.onboardingStep === 5) {
        return "/onboarding/connect-marketplaces";
      }
    }
  }

  initialLoadRedirect(userInfo: LoginModel, returnUrl: string) {
    if (userInfo) {
      if (userInfo.onboardingStep < 7) {
        return this.checkOnboarding(userInfo);
      } else if (returnUrl) {
        return returnUrl;
      } else if (permissionCount(userInfo) === 0) {
        return "settings/billing";
      } else {
        return this.dashboardRouteRedirect(userInfo);
      }
    }
  }

  dashboardRouteRedirect(userInfo) {
    if (userInfo.isNetYard) {
      return "/dashboard/product/netyard";
    } else if (userInfo.isPlanYard) {
      return "/dashboard/product/planyard";
    }else if (userInfo.isShipyard) {
      return "/dashboard/product/shipyard";
    } else if (userInfo.isPriceYard) {
      return "/dashboard/product/priceyard";
    } else {
      return "settings/billing";
    }
  }

  getProductPermission(permission) {
    var userInfo: LoginModel = this.getUserInfo();
    const value = hasPermission(userInfo, permission);
    if (value === 0) {
      const route = this.dashboardRouteRedirect(userInfo);
      this.router.navigate([route]);
      return false;
    } else {
      return true;
    }
  }

  GetCurrentUser() {
    return this.http.get(`/${this.url}/GetCurrentUser`, this.options);
  }

  GetPermissions(roleId){
    return this.http.get(`/${this.url}/GetPermissions?RoleId=${roleId}`, this.options);
  }
  
  getRolesByTenantId() {
    return this.http.get(`/${this.url}/GetRolesByTenant`, this.options);
  }
  
  SaveRolePermission(payload) {
    const data = JSON.stringify(payload);
    return this.http.post(`/${this.url}/SaveRolePermission`, payload, this.options);
  }

  addUpdateRole(payload:Role){
    return this.http.post(`/${this.url}/AddUpdateRole`, payload, this.options);
  }

  deleteRoles(id:number){
    return this.http.post(`/${this.url}/DeleteRoles`, {roleId:id}, this.options);
  }

  public deleteUserNew(payload) {
    return this.http.post(`/${this.url}/DeleteUserNew`,payload, this.options);
  }

  GetCompanyInfo(userId:number){
    return this.http.get(`/${this.url}/GetCompanyInfo?UserId=${userId}`, this.options);
  }

  UpdateCompanyInfo(payload:UpdateCompanyPayload){
    return this.http.post(`/${this.url}/UpdateCompanyInfo`,payload, this.options);
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.unsubscribe();
  }

}
